import { useState } from "react";

import { useSnackbar } from "notistack";

import { AmplitudeEvents } from "../../../common/constants/amplitude";
import { trpc } from "../../../common/helpers/trpc";

import { OrganizationPageContent } from "./components/OrganizationPageContent/OrganizationPageContent";
import { OrganizationPageOwnerBanner } from "./components/OrganizationPageOwnerBanner";

import { useLogVisitPageAmplitudeEvent } from "@/common/hooks/amplitude/useLogVisitPageAmplitudeEvent";
import { useTranslate } from "@/common/hooks/useTranslate";
import { OrganizationPageOutput } from "@/common/types/trpc";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { DashboardButton } from "@/dashboard-design-system/DashboardButton";
import {
  DashboardDialog,
  DashboardDialogBody,
  DashboardDialogContent,
  DashboardDialogFooter,
  DashboardDialogHeader,
  // eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
} from "@/dashboard-design-system/DashboardDialog";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { useBackToFormTable } from "@/features/LiveFormEditor/Navigation/useBackToFormTabs";

type OrganizationPageProps = {
  organizationPage: OrganizationPageOutput;
  isLoggedOrganization: boolean;
};

export const OrganizationPage = ({ organizationPage, isLoggedOrganization }: OrganizationPageProps) => {
  const { t } = useTranslate();
  const { enqueueSnackbar } = useSnackbar();

  const [isPublished, setIsPublished] = useState(organizationPage.isPublished);
  const [isUnpublishModalOpen, setIsUnpublishModalOpen] = useState(false);
  const { backToFormTableUrl } = useBackToFormTable();

  const { mutateAsync: unpublishOrganizationPage, isPending: isUnpublishOrganizationPageLoading } =
    trpc.unpublishOrganizationPage.useMutation();
  const { mutateAsync: publishOrganizationPage, isPending: isPublishOrganizationPageLoading } =
    trpc.publishOrganizationPage.useMutation();

  const openUnpublishModal = () => setIsUnpublishModalOpen(true);
  const closeUnpublishModal = () => setIsUnpublishModalOpen(false);

  const handleUnpublish = async () => {
    const result = await unpublishOrganizationPage({ organizationPageId: organizationPage.id });
    if (!result.error) {
      setIsPublished(false);
      closeUnpublishModal();
      enqueueSnackbar(t("organization", "snackbar.unpublishSuccess"), { vibe: "positive" });
    } else {
      enqueueSnackbar(t("organization", "snackbar.unpublishError"), { vibe: "danger" });
    }
  };

  const handlePublish = async () => {
    const result = await publishOrganizationPage({ organizationPageId: organizationPage.id });
    if (!result.error) {
      setIsPublished(true);
      enqueueSnackbar(t("organization", "snackbar.publishSuccess"), { vibe: "positive" });
    } else {
      enqueueSnackbar(t("organization", "snackbar.publishError"), { vibe: "danger" });
    }
  };

  const ownerView = isLoggedOrganization ? (
    <OrganizationPageOwnerBanner
      isPublished={isPublished}
      onPublish={handlePublish}
      onUnpublish={openUnpublishModal}
      isPublishLoading={isPublishOrganizationPageLoading}
      backToFormTableUrl={backToFormTableUrl}
    />
  ) : undefined;

  useLogVisitPageAmplitudeEvent({
    event: AmplitudeEvents.OrgPageViewed,
    payload: {
      donation_form_active: !!organizationPage.defaultDonationCampaign,
      count_upcoming_events: organizationPage.upcomingEvents.totalCount,
      count_past_events: organizationPage.pastEvents.totalCount,
      count_other_forms: organizationPage.supportForms.length,
    },
  });

  return (
    <>
      <DashboardDialog open={isUnpublishModalOpen} onClose={closeUnpublishModal} maxWidth="sm">
        <DashboardDialogBody
          slots={{
            header: <DashboardDialogHeader title={t("organization", "unpublishModal.title")} />,
            content: (
              <DashboardDialogContent>
                <DashboardTypography variant="body2">{t("organization", "unpublishModal.content")}</DashboardTypography>
              </DashboardDialogContent>
            ),
            footer: (
              <DashboardDialogFooter>
                <DashboardButton onClick={handleUnpublish} isLoading={isUnpublishOrganizationPageLoading}>
                  {t("organization", "unpublishModal.confirm")}
                </DashboardButton>
                <DashboardButton onClick={closeUnpublishModal}>{t("common", "cancel")}</DashboardButton>
              </DashboardDialogFooter>
            ),
          }}
        />
      </DashboardDialog>
      <OrganizationPageContent
        organizationPage={organizationPage}
        isPublished={isPublished}
        slots={{
          ownerView,
        }}
      />
    </>
  );
};
